import { DiffuseHalfLambertAoLmShader } from "./DiffuseHalfLambertAoLmShader";

/**
 * [DiffuseHalfLambertAoLmShader] with the lit part of the mesh brightened.
 *
 * A mesh carries both states of its texture: the pixels whose V coordinate is above
 * [LIT_V_COORDINATE] are the lit ones, and they are brightened by [LIT_BRIGHTNESS], so a lamp
 * of the scene glows where its glass is, whatever the lighting of the scene does. The
 * threshold and the multiplier are constants of the shader, not uniforms.
 *
 * The code of both shaders is kept as the copy of the shader it is derived from: the only
 * difference is the lit pixels, see the end of the fragment shader. The uniforms are the ones
 * of the shader it derives from, in the same order.
 */
export class DiffuseHalfLambertAoLmLitShader extends DiffuseHalfLambertAoLmShader {
    /** @inheritdoc */
    fillCode() {
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform mat4 model_matrix;
            uniform float fogStartDistance;
            uniform float fogDistance;
            attribute vec4 rm_Vertex;
            attribute vec2 rm_TexCoord0;
            attribute vec3 rm_Normal;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPosition;
            varying float vFogAmount;

            void main() {
              gl_Position = view_proj_matrix * rm_Vertex;
              vTextureCoord = rm_TexCoord0;
              vWorldNormal = (model_matrix * vec4(rm_Normal, 0.0)).xyz;
              vWorldPosition = (model_matrix * rm_Vertex).xyz;
              vFogAmount = clamp((length(gl_Position) - fogStartDistance) / fogDistance, 0.0, 1.0);
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPosition;
            varying float vFogAmount;

            uniform sampler2D sTexture;
            // x, y, z = direction towards the light, w = half-Lambert exponent.
            uniform vec4 lightParams;
            uniform vec3 ambientColor;
            uniform vec4 fogColor;

            uniform sampler2D aoTexture;
            uniform vec2 inverseScreenSize;

            uniform sampler2D lmTexture;
            uniform vec2 lmScale;
            /** Glow color the lightmap fades the surface towards. */
            uniform vec3 lmColor;
            /** x = height at which the glow is gone, y = height at which the fade starts. */
            uniform vec2 lmHeight;
            /** How much the lightmap glow is scaled by before it is mixed in. */
            uniform float lmBrightness;

            const float LIT_CONTRAST = 1.6;
            const float LIT_BRIGHTNESS = 0.5;

            /** Below this V coordinate a pixel of the mesh is its unlit part, above it is its lit part. */
            const float LIT_V_COORDINATE = 0.5;

            void main() {
              vec4 color = texture2D(sTexture, vTextureCoord);

            //   color.rgb *= ambientColor;

              // Screen-space ambient occlusion, applied before the lighting.
              highp vec2 aoUV = gl_FragCoord.xy * inverseScreenSize;
              float ao = texture2D(aoTexture, aoUV).r;
              color.rgb *= ao;

              // Half-Lambert directional lighting plus the ambient light, which is what
              // keeps the surfaces facing away from the light from going black.
              vec3 lightDirection = normalize(lightParams.xyz);
              float nDotL = max(dot(normalize(vWorldNormal), lightDirection), 0.0);
              float halfLambert = pow(nDotL * 0.5 + 0.5, lightParams.w);
              color.rgb *= halfLambert;

              // Baked lightmap of the scene, sampled in the world space XY plane.
              vec2 pos = vWorldPosition.xy;
              vec2 lmUV = pos * lmScale + 0.5;
              float lmCoeff = texture2D(lmTexture, lmUV).r;
              lmCoeff *= lmBrightness;
              float d = vWorldPosition.z;
              float t = clamp((d - lmHeight.y) / (lmHeight.x - lmHeight.y), 0.0, 1.0);
              float fade = 1.0 - t * t;
              lmCoeff *= fade;
              vec3 glow = mix(ambientColor, lmColor, lmCoeff);

              color.rgb *= glow;

              if (vTextureCoord.y > LIT_V_COORDINATE) {
                color.rgb = color.rgb * LIT_CONTRAST + LIT_BRIGHTNESS;
              }

              // Linear fog, applied last.
              gl_FragColor = mix(color, fogColor, vFogAmount);
            }`;
    }
}
